import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ENTREPRENEUR_TRACK, PLACEMENT_TRACK, EVENTS_DATA, MENTORSHIP_DATA, STUDENT_PAGE_DATA
} from "@/data/siteData";
import {
  Rocket, Briefcase, Calendar, Users, MessageSquare,
  ArrowRight, Quote, ChevronLeft, ChevronRight, Award,
  MapPin, BookOpen, Zap, Target
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { supabase } from "@/lib/supabaseClient";

const contentIconMap = { Users, Target, MessageSquare, Award, Zap, BookOpen, Briefcase };

const TimelineStage = ({ stage, title, description, index }) => (
  <ScrollReveal delay={index * 0.05}>
    <div className="relative pl-14 pb-10 last:pb-0">
      <div className="timeline-line" />
      <div className="timeline-dot" style={{ top: "4px" }} />
      <div>
        <span className="text-xs font-bold text-[#097DC3] uppercase tracking-wider">Stage {stage}</span>
        <h4 className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] mt-1 mb-2">{title}</h4>
        <p className="text-sm text-[#475569] leading-relaxed">{description}</p>
      </div>
    </div>
  </ScrollReveal>
);

export default function StudentEmpowerment() {
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const [formData, setFormData] = useState({ name: "", role: "", category: "student", program: "", outcome: "" });

  const [studentTestimonials, setStudentTestimonials] = useState([]);
  const [loadingTestimonials, setLoadingTestimonials] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoadingTestimonials(true);
      try {
        const { data, error } = await supabase
          .from("testimonials")
          .select("id,name,role,category,program,outcome,status")
          .eq("status", "approved")
          .eq("category", "student")
          .order("approved_at", { ascending: false });

        if (error) throw error;
        if (cancelled) return;
        setStudentTestimonials(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setStudentTestimonials([]);
      } finally {
        if (!cancelled) setLoadingTestimonials(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (studentTestimonials.length === 0) return;
    const interval = setInterval(() => {
      setTestimonialIdx((p) => (p + 1) % studentTestimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [studentTestimonials.length]);

  useEffect(() => {
    setTestimonialIdx(0);
  }, [studentTestimonials.length]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        name: formData.name,
        role: formData.role,
        category: formData.category,
        program: formData.program,
        outcome: formData.outcome,
        status: "pending",
      };

      const { error } = await supabase.from("testimonials").insert(payload);
      if (error) throw error;
      toast.success("Thank you for sharing your experience! Your testimonial has been submitted.");
      setFormData({ name: "", role: "", category: "", program: "", outcome: "" });
    } catch {
      toast.error("Could not submit right now. Please try again later.");
    }
  };

  return (
    <div>
      <Toaster position="top-right" />

      <section className="relative py-24 md:py-32 overflow-hidden mesh-bg" data-testid="student-hero">
        <div className="absolute inset-0 grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#097DC3]/8 text-[#097DC3] text-sm font-semibold uppercase tracking-wider mb-6">
              {STUDENT_PAGE_DATA.hero.tagline}
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#0A0A0A] mb-6">
              {STUDENT_PAGE_DATA.hero.titlePrefix} <span className="text-[#097DC3]">{STUDENT_PAGE_DATA.hero.titleHighlight}</span> {STUDENT_PAGE_DATA.hero.titleSuffix}
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-[#475569] max-w-2xl">
              {STUDENT_PAGE_DATA.hero.subtitle}
            </p>
          </motion.div>
        </div>
        <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-[#8CDDF6]/15 blur-3xl" />
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-b from-[#DBEEFF] via-white to-[#DBEEFF]" data-testid="student-tabs-section">
        <div className="max-w-6xl mx-auto px-4 md:px-12">
          <ScrollReveal>
            <div className="text-center mb-10">
              <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-3">
                {STUDENT_PAGE_DATA.programsIntro.title}
              </h2>
              <p className="text-[#475569] text-base md:text-lg">
                {STUDENT_PAGE_DATA.programsIntro.subtitle}
              </p>
            </div>
          </ScrollReveal>
          <Tabs defaultValue="entrepreneur" className="w-full">
            <TabsList className="w-full grid grid-cols-2 lg:grid-cols-4 h-auto gap-4 bg-transparent p-0 mb-14">
              <TabsTrigger
                value="entrepreneur"
                className="group relative flex flex-col items-center gap-2 px-4 py-5 rounded-2xl border-2 border-slate-200 bg-white shadow-sm cursor-pointer transition-all duration-200 hover:border-[#097DC3]/40 hover:shadow-md data-[state=active]:border-[#097DC3] data-[state=active]:bg-[#097DC3] data-[state=active]:text-white data-[state=active]:shadow-lg text-sm font-medium"
                data-testid="tab-entrepreneur"
              >
                <div className="w-10 h-10 rounded-xl bg-[#097DC3]/10 group-data-[state=active]:bg-white/20 flex items-center justify-center transition-colors">
                  <Rocket size={20} className="text-[#097DC3] group-data-[state=active]:text-white" />
                </div>
                <span className="font-semibold">{STUDENT_PAGE_DATA.tabs.entrepreneur.label}</span>
              </TabsTrigger>
              <TabsTrigger
                value="placement"
                className="group relative flex flex-col items-center gap-2 px-4 py-5 rounded-2xl border-2 border-slate-200 bg-white shadow-sm cursor-pointer transition-all duration-200 hover:border-[#097DC3]/40 hover:shadow-md data-[state=active]:border-[#097DC3] data-[state=active]:bg-[#097DC3] data-[state=active]:text-white data-[state=active]:shadow-lg text-sm font-medium"
                data-testid="tab-placement"
              >
                <div className="w-10 h-10 rounded-xl bg-[#097DC3]/10 group-data-[state=active]:bg-white/20 flex items-center justify-center transition-colors">
                  <Briefcase size={20} className="text-[#097DC3] group-data-[state=active]:text-white" />
                </div>
                <span className="font-semibold">{STUDENT_PAGE_DATA.tabs.placement.label}</span>
              </TabsTrigger>
              <TabsTrigger
                value="events"
                className="group relative flex flex-col items-center gap-2 px-4 py-5 rounded-2xl border-2 border-slate-200 bg-white shadow-sm cursor-pointer transition-all duration-200 hover:border-[#097DC3]/40 hover:shadow-md data-[state=active]:border-[#097DC3] data-[state=active]:bg-[#097DC3] data-[state=active]:text-white data-[state=active]:shadow-lg text-sm font-medium"
                data-testid="tab-events"
              >
                <div className="w-10 h-10 rounded-xl bg-[#097DC3]/10 group-data-[state=active]:bg-white/20 flex items-center justify-center transition-colors">
                  <Calendar size={20} className="text-[#097DC3] group-data-[state=active]:text-white" />
                </div>
                <span className="font-semibold">{STUDENT_PAGE_DATA.tabs.events.label}</span>
              </TabsTrigger>
              <TabsTrigger
                value="mentorship"
                className="group relative flex flex-col items-center gap-2 px-4 py-5 rounded-2xl border-2 border-slate-200 bg-white shadow-sm cursor-pointer transition-all duration-200 hover:border-[#097DC3]/40 hover:shadow-md data-[state=active]:border-[#097DC3] data-[state=active]:bg-[#097DC3] data-[state=active]:text-white data-[state=active]:shadow-lg text-sm font-medium"
                data-testid="tab-mentorship"
              >
                <div className="w-10 h-10 rounded-xl bg-[#097DC3]/10 group-data-[state=active]:bg-white/20 flex items-center justify-center transition-colors">
                  <Users size={20} className="text-[#097DC3] group-data-[state=active]:text-white" />
                </div>
                <span className="font-semibold">{STUDENT_PAGE_DATA.tabs.mentorship.label}</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="entrepreneur" data-testid="content-entrepreneur">
              <ScrollReveal>
                <div className="mb-12">
                  <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-4">
                    {STUDENT_PAGE_DATA.tabs.entrepreneur.title}
                  </h2>
                  <p className="text-base md:text-lg text-[#475569] max-w-2xl">
                    {STUDENT_PAGE_DATA.tabs.entrepreneur.description}
                  </p>
                </div>
              </ScrollReveal>
              <div className="max-w-2xl">
                {ENTREPRENEUR_TRACK.map((s, i) => (
                  <TimelineStage key={s.stage} {...s} index={i} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="placement" data-testid="content-placement">
              <ScrollReveal>
                <div className="mb-12">
                  <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-4">
                    {STUDENT_PAGE_DATA.tabs.placement.title}
                  </h2>
                  <p className="text-base md:text-lg text-[#475569] max-w-2xl">
                    {STUDENT_PAGE_DATA.tabs.placement.description}
                  </p>
                </div>
              </ScrollReveal>
              <div className="max-w-2xl">
                {PLACEMENT_TRACK.map((s, i) => (
                  <TimelineStage key={s.stage} {...s} index={i} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="events" data-testid="content-events">
              <ScrollReveal>
                <div className="mb-12">
                  <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-4">
                    {STUDENT_PAGE_DATA.tabs.events.title}
                  </h2>
                  <p className="text-base md:text-lg text-[#475569] max-w-2xl">
                    {STUDENT_PAGE_DATA.tabs.events.description}
                  </p>
                </div>
              </ScrollReveal>

              <ScrollReveal className="mb-16">
                <h3 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A] mb-8">{STUDENT_PAGE_DATA.tabs.events.hackathonsTitle}</h3>
                <p className="text-base text-[#475569] leading-relaxed mb-8 max-w-3xl">
                  {STUDENT_PAGE_DATA.tabs.events.hackathonsDescription}
                </p>
                <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {STUDENT_PAGE_DATA.tabs.events.hackathonHighlights.map((item) => {
                    const Icon = contentIconMap[item.icon];
                    return (
                      <StaggerItem key={item.title}>
                        <div className="card-lift bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                          <div className="w-10 h-10 rounded-xl bg-[#097DC3]/10 flex items-center justify-center mb-4">
                            <Icon size={20} className="text-[#097DC3]" />
                          </div>
                          <h4 className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] mb-2">{item.title}</h4>
                          <p className="text-sm text-[#475569] leading-relaxed">{item.description}</p>
                        </div>
                      </StaggerItem>
                    );
                  })}
                </StaggerContainer>
              </ScrollReveal>

              <ScrollReveal className="mb-16">
                <h3 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A] mb-8">{STUDENT_PAGE_DATA.tabs.events.workshopsTitle}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {EVENTS_DATA.workshops.map((w) => (
                    <div key={w} className="flex items-center gap-3 p-4 rounded-xl bg-[#F8FAFC] border border-slate-100">
                      <BookOpen size={18} className="text-[#097DC3] shrink-0" />
                      <span className="text-sm font-medium text-[#0A0A0A]">{w}</span>
                    </div>
                  ))}
                </div>
              </ScrollReveal>

              <ScrollReveal className="mb-12">
                <h3 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A] mb-8">{STUDENT_PAGE_DATA.tabs.events.collegeTitle}</h3>
                <p className="text-base text-[#475569] leading-relaxed mb-8 max-w-3xl">
                  {STUDENT_PAGE_DATA.tabs.events.collegeDescription}
                </p>
                <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {STUDENT_PAGE_DATA.tabs.events.collegePillars.map((item) => {
                    const Icon = contentIconMap[item.icon];
                    return (
                      <StaggerItem key={item.title}>
                        <div className="card-lift bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                          <div className="w-10 h-10 rounded-xl bg-[#3DC0E7]/10 flex items-center justify-center mb-4">
                            <Icon size={20} className="text-[#3DC0E7]" />
                          </div>
                          <h4 className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] mb-2">{item.title}</h4>
                          <p className="text-sm text-[#475569] leading-relaxed">{item.description}</p>
                        </div>
                      </StaggerItem>
                    );
                  })}
                </StaggerContainer>
              </ScrollReveal>
            </TabsContent>

            <TabsContent value="mentorship" data-testid="content-mentorship">
              <ScrollReveal>
                <div className="mb-12">
                  <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight text-[#0A0A0A] mb-4">
                    {STUDENT_PAGE_DATA.tabs.mentorship.title}
                  </h2>
                  <p className="text-base md:text-lg text-[#475569] max-w-2xl">
                    {STUDENT_PAGE_DATA.tabs.mentorship.description}
                  </p>
                </div>
              </ScrollReveal>

              <ScrollReveal className="mb-16">
                <h3 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A] mb-8">{STUDENT_PAGE_DATA.tabs.mentorship.howWeMentorTitle}</h3>
                <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {STUDENT_PAGE_DATA.tabs.mentorship.howWeMentor.map((item) => {
                    const Icon = contentIconMap[item.icon];
                    return (
                      <StaggerItem key={item.title}>
                        <div className="card-lift bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                          <div className="w-12 h-12 rounded-full bg-[#097DC3]/10 flex items-center justify-center mb-4">
                            <Icon size={22} className="text-[#097DC3]" />
                          </div>
                          <h4 className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] mb-2">{item.title}</h4>
                          <p className="text-sm text-[#475569] leading-relaxed">{item.description}</p>
                        </div>
                      </StaggerItem>
                    );
                  })}
                </StaggerContainer>
              </ScrollReveal>

              <ScrollReveal>
                <h3 className="font-['Outfit'] text-2xl font-semibold text-[#0A0A0A] mb-8">{STUDENT_PAGE_DATA.tabs.mentorship.brandingTitle}</h3>
                <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {MENTORSHIP_DATA.programs.map((p) => (
                    <StaggerItem key={p.title}>
                      <div className="card-lift bg-[#F8FAFC] p-6 rounded-2xl border border-slate-100">
                        <h4 className="font-['Outfit'] text-lg font-semibold text-[#0A0A0A] mb-3">{p.title}</h4>
                        <p className="text-sm text-[#475569] leading-relaxed">{p.description}</p>
                      </div>
                    </StaggerItem>
                  ))}
                </StaggerContainer>
              </ScrollReveal>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <section
  className="py-12 md:py-16 relative bg-fixed bg-center bg-cover"
  style={{
    backgroundImage: STUDENT_PAGE_DATA.testimonials.backgroundImage,
      backgroundopacity: 0.1,
  }} data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="text-center mb-12">
              <span className="text-sm font-semibold uppercase tracking-wider text-[#097DC3] mb-3 block">{STUDENT_PAGE_DATA.testimonials.sectionLabel}</span>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <ScrollReveal>
              <div>
                <h3 className="font-['Outfit'] text-2xl font-semibold tracking-tight text-[#0A0A0A] mb-4">
                  {STUDENT_PAGE_DATA.testimonials.formTitle}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="testimonial-form">
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{STUDENT_PAGE_DATA.testimonials.fields.fullName}</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow"
                      placeholder={STUDENT_PAGE_DATA.testimonials.fields.fullNamePlaceholder}
                      required
                      data-testid="testimonial-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{STUDENT_PAGE_DATA.testimonials.fields.schoolCollege}</label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow"
                      placeholder={STUDENT_PAGE_DATA.testimonials.fields.schoolCollegePlaceholder}
                      required
                      data-testid="testimonial-role"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{STUDENT_PAGE_DATA.testimonials.fields.programUsed}</label>
                    <input
                      type="text"
                      value={formData.program}
                      onChange={(e) => setFormData({ ...formData, program: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow"
                      placeholder={STUDENT_PAGE_DATA.testimonials.fields.programUsedPlaceholder}
                      required
                      data-testid="testimonial-program"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">{STUDENT_PAGE_DATA.testimonials.fields.outcome}</label>
                    <textarea
                      value={formData.outcome}
                      onChange={(e) => setFormData({ ...formData, outcome: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#097DC3]/30 focus:border-[#097DC3] transition-shadow resize-none"
                      placeholder={STUDENT_PAGE_DATA.testimonials.fields.outcomePlaceholder}
                      required
                      data-testid="testimonial-outcome"
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn-pill bg-[#097DC3] text-white px-8 py-3 rounded-full font-semibold text-sm"
                    data-testid="testimonial-submit"
                  >
                    {STUDENT_PAGE_DATA.testimonials.submitButton}
                  </button>
                </form>
              </div>
            </ScrollReveal>


            <ScrollReveal direction="right">
              <div>
                <h3 className="font-['Outfit'] text-2xl font-semibold tracking-tight text-[#0A0A0A] mb-8">
                  {STUDENT_PAGE_DATA.testimonials.storiesTitle}
                </h3>
                <div className="bg-white p-8 rounded-2xl border-l-4 border-[#3DC0E7] shadow-sm min-h-[400px] flex flex-col justify-between">
                  {loadingTestimonials ? (
                    <div>
                      <Quote size={32} className="text-[#8CDDF6] mb-6" />
                      <p className="text-lg text-[#475569] leading-relaxed mb-8" data-testid="student-testimonial-quote">
                        {STUDENT_PAGE_DATA.testimonials.loading}
                      </p>
                    </div>
                  ) : studentTestimonials.length === 0 ? (
                    <div>
                      <Quote size={32} className="text-[#8CDDF6] mb-6" />
                      <p className="text-lg text-[#475569] leading-relaxed mb-8" data-testid="student-testimonial-quote">
                        {STUDENT_PAGE_DATA.testimonials.empty}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Quote size={32} className="text-[#8CDDF6] mb-6" />
                        <p className="text-lg text-[#0A0A0A] leading-relaxed mb-8" data-testid="student-testimonial-quote">
                          {studentTestimonials[testimonialIdx]?.outcome}
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center gap-4 mb-6">
                          <img
                            src={STUDENT_PAGE_DATA.testimonials.avatarSrc}
                            alt={studentTestimonials[testimonialIdx]?.name || STUDENT_PAGE_DATA.testimonials.avatarAlt}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div>
                            <p className="font-semibold text-[#0A0A0A]">{studentTestimonials[testimonialIdx]?.name}</p>
                            <p className="text-sm text-[#475569]">{studentTestimonials[testimonialIdx]?.role}</p>
                            <p className="text-xs text-[#097DC3] font-medium mt-0.5">{studentTestimonials[testimonialIdx]?.program}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-2">
                            {studentTestimonials.map((_, i) => (
                              <button
                                key={studentTestimonials[i]?.id || i}
                                onClick={() => setTestimonialIdx(i)}
                                className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${i === testimonialIdx ? "bg-[#097DC3]" : "bg-[#E2E8F0]"}`}
                                data-testid={`student-testimonial-dot-${i}`}
                              />
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setTestimonialIdx((p) => (p - 1 + studentTestimonials.length) % studentTestimonials.length)}
                              className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                              data-testid="student-testimonial-prev"
                            >
                              <ChevronLeft size={18} />
                            </button>
                            <button
                              onClick={() => setTestimonialIdx((p) => (p + 1) % studentTestimonials.length)}
                              className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                              data-testid="student-testimonial-next"
                            >
                              <ChevronRight size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
